import { match } from "ts-pattern";
import { ref } from "vue";
import type { LocationQuery, RouteParams, RouteRecordName } from "vue-router";
import { RouteLocationRaw } from "vue-router";

import type {
  Controller,
  Event,
  GraphDatabaseDictionary,
  ObjectIdRaw,
  OrgId,
  ReferenceId,
  ReferenceKind,
} from "@ctrl/controller-rpc";

import type { PlannerLayout } from "@/components/planner/controllers/planner";
import type { Menu } from "@/composables/menuTypes";
import type { Profile } from "@/composables/useProfile";
import {
  makeAgentReference,
  makeAutomationReference,
  makeConversationReference,
  makeOrganizationReference,
  makeTableReference,
  makeWorkspaceReference,
} from "@/references/ids";
import { RouteNames } from "@/routes/types";
import type { Router } from "@/types";
import { formatKey } from "@/utils/date-key";
import { history } from "@/utils/history";
import {
  internalPathFromRoutineId,
  isRoutinePrefixedId,
  referenceIdFromString,
} from "@/utils/hyperlink";
import { isBoundTaskId } from "@/utils/id";
import { in_electron } from "@/utils/in";
import { scrolling } from "@/utils/scrolling";

export const firstRouteParam = (
  value: string | string[] | undefined
): string | undefined => (Array.isArray(value) ? value[0] : value);

export function shouldOpenInNewWindow(
  event?: MouseEvent | KeyboardEvent
): boolean {
  return (
    // No idea why we support the Alt key to open in a new tab
    !!event?.altKey ||
    // `Ctrl` is the standard on Windows/Linux
    !!event?.ctrlKey ||
    // `Meta` is the standard on MacOS
    !!event?.metaKey ||
    // Middle mouse click is the standard on Windows/Linux
    (typeof MouseEvent !== "undefined" &&
      event instanceof MouseEvent &&
      event.button === 1)
  );
}

export function resolveRouteFullPath(
  router: Router,
  to: RouteLocationRaw | string
): string {
  return (typeof to === "string" ? router.resolve(to) : router.resolve(to))
    .fullPath;
}

export const navigateForward = async (router: Router) => {
  // Fetch the next history entry.
  const next = history.next();
  // Guard against navigation to invalid routes.
  if (!next) {
    window.console.warn("[navigate] cannot go forward; no history available");
    return;
  }
  // Do not navigate to the same route
  if (next.route.fullPath == router.currentRoute.value.fullPath) {
    return;
  }
  // Leave the current route, capturing the scrolling positions and updating the history.
  const positions = scrolling.capture(router.currentRoute.value);
  history.leave(router.currentRoute.value, positions, "forward");
  // Trigger the router to navigate to the next route.
  try {
    await router.push(next.route);
  } catch (e) {
    throw Error("Navigation error", { cause: e });
  }
};

/**
 * Navigate to the user's configured home menu item (or the first visible
 * menu item, or Agenda as a last resort).
 */
export const navigateToHome = async (router: Router, menu: Menu) => {
  const homeIndex = menu.visibleItems.value.findIndex((item) =>
    menu.isHomeItem(item)
  );
  if (homeIndex >= 0) {
    await menu.navigateToMenuItem(homeIndex);
  } else {
    const firstIndex = menu.findFirstMenuIndex();
    if (firstIndex >= 0) {
      await menu.navigateToMenuItem(firstIndex);
    } else {
      await navigateTo(RouteNames.Agenda, router);
    }
  }
};

/**
 * Navigate to the previous history entry.
 *
 * Falls back to navigateToHome() if there is no usable history entry
 * (e.g. the page was opened via deep-link).
 */
export const navigateBackward = async (router: Router, menu: Menu) => {
  // Fetch the previous history entry.
  const previous = history.previous();
  // Guard against navigation to invalid routes.
  if (!previous) {
    await navigateToHome(router, menu);
    return;
  }
  // Do not navigate to the same route
  if (previous.route.fullPath == router.currentRoute.value.fullPath) {
    await navigateToHome(router, menu);
    return;
  }
  // Leave the current route, capturing the scrolling position and updating the history.
  const positions = scrolling.capture(router.currentRoute.value);
  history.leave(router.currentRoute.value, positions, "backward");
  // Trigger the router to navigate to the previous route.
  try {
    await router.push(previous.route);
  } catch (e) {
    throw Error("Navigation error", { cause: e });
  }
};

// Do not use this when navigating to a Page/Event/Task/Person, use navigateToObject instead or navigateToEvent when
// navigating to an Event
export const navigateTo = async (
  destination: RouteNames | RouteRecordName,
  router: Router,
  payload?: { params?: RouteParams; query?: LocationQuery; hash?: string },
  event?: MouseEvent | KeyboardEvent
) => {
  const to: RouteLocationRaw = {
    name: destination,
    ...(payload?.params && { params: payload.params }),
    ...(payload?.query && { query: payload.query }),
    ...(payload?.hash && { hash: payload.hash }),
  };
  // Satellite windows (e.g. the dashboard, see the console window's App.vue)
  // provide a minimal Router whose `push` forwards the route to the main
  // window over IPC. There is nothing to resolve or bookkeep locally: the
  // main window re-enters navigateTo with its real router on reception.
  if (typeof router.resolve !== "function") {
    await router.push(to);
    return;
  }
  if (shouldOpenInNewWindow(event)) {
    await openRouteInNewWindow(router, to);
    return;
  }
  const resolved = router.resolve(to);

  // Do not navigate to the same location (including query/hash).
  // This avoids creating duplicate history entries and prevents some components
  // from re-entering loading states on redundant navigations. It also prevents
  // our custom `history.leave(...)` from running when the router would treat the
  // navigation as a no-op.
  if (resolved.fullPath === router.currentRoute.value.fullPath) {
    return;
  }
  // Fetch the current history entry.
  const current = history.current();
  // Guard against navigation to invalid routes.
  if (!current) {
    window.console.warn("[navigate] cannot navigate to; no history available");
    return;
  }
  // Leave the current route, capturing the scrolling positions and updating the history.
  const positions = scrolling.capture(router.currentRoute.value);
  history.leave(router.currentRoute.value, positions, "new");
  try {
    // Trigger the router to navigate to the new route.
    await router.push(to);
  } catch (e) {
    throw Error("Navigation error", { cause: e });
  }
};

export const navigateToId = async (
  router: Router,
  controller: Controller,
  defaultTypes: GraphDatabaseDictionary,
  workspaceId: string,
  tableId: string,
  rowId: string,
  event?: MouseEvent | KeyboardEvent
) =>
  defaultTypes[tableId]
    ? match(defaultTypes[tableId].name)
        .with("tasks", () =>
          navigateToObject(
            router,
            controller,
            { kind: "task", id: rowId },
            event
          )
        )
        .with("task-rec", () =>
          navigateToObject(
            router,
            controller,
            { kind: "task-rec", id: rowId },
            event
          )
        )
        .with("events", () =>
          navigateToObject(
            router,
            controller,
            { kind: "event", id: rowId },
            event
          )
        )
        .with("pages", () =>
          navigateToObject(
            router,
            controller,
            { kind: "page", id: rowId },
            event
          )
        )
        .with("people", () =>
          navigateToObject(
            router,
            controller,
            { kind: "people", id: rowId },
            event
          )
        )
        .otherwise(() =>
          navigateToCustomObject(router, workspaceId, tableId, rowId, event)
        )
    : navigateToCustomObject(router, workspaceId, tableId, rowId, event);

export const navigateToCustomObject = async (
  router: Router,
  workspaceId: string,
  tableId: string,
  rowId: string,
  event?: MouseEvent | KeyboardEvent,
  query?: LocationQuery,
  hash?: string
) => {
  await navigateTo(
    RouteNames.Object,
    router,
    {
      params: { workspaceId, tableId, rowId },
      query,
      hash,
    },
    event
  );
};

export const navigateToAutomation = async (
  router: Router,
  objectId: string,
  organizationId?: string,
  event?: MouseEvent | KeyboardEvent
) => {
  await navigateTo(
    RouteNames.Automation,
    router,
    {
      params: { objectId },
      query: organizationId ? { organizationId } : undefined,
    },
    event
  );
};

export const navigateToDatabase = async (
  router: Router,
  workspaceId: string,
  databaseId: string,
  query?: LocationQuery,
  event?: MouseEvent | KeyboardEvent
) =>
  navigateTo(
    RouteNames.Database,
    router,
    {
      params: {
        workspaceId,
        databaseId,
      },
      ...(query ? { query } : {}),
    },
    event
  );

export const navigateToAgent = async (
  router: Router,
  agentId: string,
  event?: MouseEvent | KeyboardEvent
): Promise<void> => {
  await navigateTo(
    RouteNames.Agent,
    router,
    {
      params: { agentId },
    },
    event
  );
};

export const navigateToAgentConversation = async (
  router: Router,
  agentId: string,
  conversationId: string
): Promise<void> => {
  await navigateTo(RouteNames.AgentConversation, router, {
    params: { agentId },
    query: { id: conversationId },
  });
};

export const navigateToAgentConfiguration = async (
  router: Router,
  agentId: string
): Promise<void> => {
  await navigateTo(RouteNames.AgentConfiguration, router, {
    params: { agentId },
  });
};

export const replaceAgentConversation = async (
  router: Router,
  agentId: string,
  conversationId: string
): Promise<void> => {
  await router.replace({
    name: RouteNames.AgentConversation,
    params: { agentId },
    query: { id: conversationId },
  });
};

export const navigateToType = async (
  router: Router,
  orgId: string,
  workspaceId: string,
  id?: string,
  query?: LocationQuery,
  event?: MouseEvent | KeyboardEvent
) => {
  const destination = id
    ? RouteNames.SettingsTypeEdition
    : RouteNames.SettingsTypeCreation;
  return navigateTo(
    destination,
    router,
    {
      params: {
        orgId,
        workspaceId,
        ...(id ? { id } : {}),
      },
      ...(query ? { query } : {}),
    },
    event
  );
};

const personalOrganizationId = async (controller: Controller): Promise<OrgId> =>
  (await controller.auth.id()).replace("account:", "org:");

type GraphObjectRouteParams = {
  workspaceId: string;
  tableId: string;
  rowId: string;
};

export type PageRouteTarget =
  | {
      name: RouteNames.OrgPage;
      params: { orgId: OrgId; pageId: string };
    }
  | {
      name: RouteNames.Object;
      params: GraphObjectRouteParams;
    };

const graphObjectRouteParams = async (
  controller: Controller,
  id: string
): Promise<GraphObjectRouteParams | null> => {
  let parsed: ObjectIdRaw;
  try {
    parsed = await controller.graph.object.id.parse(id);
  } catch {
    return null;
  }
  if (!parsed.workspace_id && !parsed.table_id && !parsed.row_id) return null;
  if (!parsed.workspace_id || !parsed.table_id || !parsed.row_id) {
    throw new Error(`Invalid graph object ID: ${id}`);
  }
  return {
    workspaceId: parsed.workspace_id,
    tableId: parsed.table_id,
    rowId: parsed.row_id,
  };
};

export const resolvePageRouteTarget = async (
  controller: Controller,
  pageId: string
): Promise<PageRouteTarget> => {
  const graphParams = await graphObjectRouteParams(controller, pageId);
  if (graphParams) {
    return {
      name: RouteNames.Object,
      params: graphParams,
    };
  }
  return {
    name: RouteNames.OrgPage,
    params: {
      orgId: await personalOrganizationId(controller),
      pageId,
    },
  };
};

const organizationIdForWorkspace = async (
  controller: Controller,
  workspaceId: string
): Promise<OrgId | undefined> => {
  const organizationIdsRef = await controller.organization.all();
  const organizationIds = organizationIdsRef.release() ?? [];
  const matches = await Promise.all(
    organizationIds.map(async (organizationId) => {
      const workspaceIdsRef =
        await controller.organization.workspaces.all(organizationId);
      const workspaceIds = workspaceIdsRef.release() ?? [];
      return workspaceIds.includes(workspaceId) ? organizationId : undefined;
    })
  );
  return matches.find((organizationId) => organizationId !== undefined);
};

const allOrganizationIds = async (controller: Controller): Promise<OrgId[]> => {
  const organizationIdsRef = await controller.organization.all();
  return organizationIdsRef.release() ?? [];
};

/**
 * Which organization owns an agent. Copy-link URLs omit `organizationId`
 * (it is redundant for navigation), so converting a pasted `/agents/…`
 * path to an `agent_ref` has to recover the org the same way the agent
 * route does: look the id up in each org's agent list, then treat
 * `assistant:` as the personal organization.
 */
const organizationIdForAgent = async (
  controller: Controller,
  agentId: string
): Promise<OrgId | undefined> => {
  const ids = await allOrganizationIds(controller);
  const matches = await Promise.all(
    ids.map(async (organizationId) => {
      const agentIdsRef = await controller.ai.agent.all(organizationId);
      const agentIds = agentIdsRef.release() ?? [];
      return agentIds.includes(agentId) ? organizationId : undefined;
    })
  );
  const found = matches.find((organizationId) => organizationId !== undefined);
  if (found) return found;
  if (agentId.startsWith("assistant:")) {
    return personalOrganizationId(controller);
  }
  return undefined;
};

/**
 * Which organization owns an automation. Automation copy-links include
 * `organizationId`, but pasted `/automations/…` paths that omit it still
 * need a lookup to compose an `automation_ref`.
 */
const organizationIdForAutomation = async (
  controller: Controller,
  automationId: string
): Promise<OrgId | undefined> => {
  const ids = await allOrganizationIds(controller);
  const matches = await Promise.all(
    ids.map(async (organizationId) => {
      const automationIdsRef =
        await controller.ai.automation.all(organizationId);
      const automationIds = automationIdsRef.release() ?? [];
      return automationIds.includes(automationId) ? organizationId : undefined;
    })
  );
  return matches.find((organizationId) => organizationId !== undefined);
};

/**
 * Resolve native and graph task/page references to the organization that owns
 * their route. Native objects belong to the personal organization; graph
 * objects carry a workspace in their encoded ID.
 */
export const resolveReferenceOrganizationId = async (
  controller: Controller,
  reference: Pick<ReferenceId, "id" | "kind">
): Promise<OrgId> => {
  if (reference.kind === "task" || reference.kind === "page") {
    let workspaceId: string | undefined;
    try {
      const parsed = await controller.graph.object.id.parse(reference.id);
      workspaceId = parsed.workspace_id ?? undefined;
    } catch {
      // Native task and page IDs are not graph object IDs.
    }
    if (workspaceId) {
      const organizationId = await organizationIdForWorkspace(
        controller,
        workspaceId
      );
      if (organizationId) return organizationId;
      throw new Error(`No organization owns workspace ${workspaceId}`);
    }
  }
  return personalOrganizationId(controller);
};

/**
 * Bound tasks — graph rows (`object:…`) surfaced as generalized tasks — always
 * display as their custom object screen (`Object.vue` renders the completion
 * checkbox); the task screen only hosts native tasks. Normalize a task
 * reference carrying a bound id into the object reference it really is, so
 * every navigation and built link lands on the object route.
 * `routes/Task.vue` guards direct URL entry the same way.
 */
const normalizeBoundTaskReference = (id: ReferenceId): ReferenceId =>
  id.kind === "task" && isBoundTaskId(id.id)
    ? { kind: "object", id: id.id }
    : id;

export const getObjectRoutePath = async (
  router: Router,
  controller: Controller,
  reference: ReferenceId
): Promise<string> => {
  const id = normalizeBoundTaskReference(reference);
  switch (id.kind) {
    case "task-rec":
      return resolveRouteFullPath(router, {
        name: RouteNames.RecurrentTask,
        params: { id: id.id },
      });
    case "task": {
      const orgId = await resolveReferenceOrganizationId(controller, id);
      return resolveRouteFullPath(router, {
        name: RouteNames.OrgTask,
        params: { orgId, taskId: id.id },
      });
    }
    case "event":
      return resolveRouteFullPath(router, {
        name: RouteNames.Event,
        params: { id: id.id },
      });
    case "event-rec": {
      const first_occurrence = await controller.event.recurrent.firstOccurrence(
        id.id
      );
      if (first_occurrence.value) {
        const occurrenceId = first_occurrence.value.id;
        first_occurrence.release();
        return resolveRouteFullPath(router, {
          name: RouteNames.Event,
          params: { id: occurrenceId },
        });
      }
      throw new Error("Could not find first occurrence of recurring event");
    }
    case "page": {
      const target = await resolvePageRouteTarget(controller, id.id);
      return resolveRouteFullPath(router, target);
    }
    case "people":
      return resolveRouteFullPath(router, {
        name: RouteNames.Person,
        params: { id: id.id },
      });
    case "object": {
      const { workspace_id, table_id, row_id } =
        await controller.graph.object.id.parse(id.id);
      return resolveRouteFullPath(router, {
        name: RouteNames.Object,
        params: {
          workspaceId: workspace_id,
          tableId: table_id,
          rowId: row_id,
        },
      });
    }
    case "organization":
      return resolveRouteFullPath(router, {
        name: RouteNames.Organization,
        params: { orgId: id.id },
      });
    case "table_ref": {
      const { workspace_id: workspaceId, table_id: databaseId } =
        await controller.id.tableRef.parse(id.id);
      return router.resolve({
        name: RouteNames.Database,
        params: { workspaceId, databaseId },
      }).href;
    }
    case "workspace_ref": {
      const { org_id: orgId, workspace_id: workspaceId } =
        await controller.id.workspaceRef.parse(id.id);
      return router.resolve({
        name: RouteNames.Workspace,
        params: { orgId, workspaceId },
      }).href;
    }
    case "agent": {
      const { org_id: organizationId, agent_id: agentId } =
        await controller.id.agentRef.parse(id.id);
      return router.resolve({
        name: RouteNames.Agent,
        params: { agentId },
        query: { organizationId },
      }).href;
    }
    case "automation": {
      const { org_id: organizationId, automation_id: automationId } =
        await controller.id.automationRef.parse(id.id);
      return router.resolve({
        name: RouteNames.Automation,
        params: { objectId: automationId },
        query: { organizationId },
      }).href;
    }
    case "conversation": {
      const {
        org_id: organizationId,
        agent_id: agentId,
        conversation_id: conversationId,
      } = await controller.id.conversationRef.parse(id.id);
      return router.resolve({
        name: RouteNames.AgentConversation,
        params: { agentId },
        query: { id: conversationId, organizationId },
      }).href;
    }
    case "automation_run": {
      const {
        org_id: organizationId,
        automation_id: automationId,
        automation_run_id: runId,
      } = await controller.id.automationRunRef.parse(id.id);
      return router.resolve({
        name: RouteNames.AutomationRunDetail,
        params: { objectId: automationId, runId },
        query: { organizationId },
      }).href;
    }
  }
};

export const navigateToObject = async (
  router: Router,
  controller: Controller,
  reference: ReferenceId | null,
  event?: MouseEvent | KeyboardEvent,
  query?: LocationQuery,
  hash?: string
) => {
  // Bound tasks route to their object screen, whatever kind the caller
  // passed — see normalizeBoundTaskReference.
  const id = reference && normalizeBoundTaskReference(reference);
  if (shouldOpenInNewWindow(event) && id) {
    const fullPath = await getObjectRoutePath(router, controller, id);
    await openRouteInNewWindow(router, fullPath);
    return;
  }
  if (id) {
    //controller.useDestination(id);
    switch (id.kind) {
      case "task-rec":
        await navigateTo(
          RouteNames.RecurrentTask,
          router,
          {
            params: { id: id.id },
            query,
            hash,
          },
          event
        );
        break;
      case "task": {
        const orgId = await resolveReferenceOrganizationId(controller, id);
        await navigateTo(
          RouteNames.OrgTask,
          router,
          {
            params: { orgId, taskId: id.id },
            query,
            hash,
          },
          event
        );
        break;
      }
      case "event":
        await navigateTo(
          RouteNames.Event,
          router,
          {
            params: { id: id.id },
            query,
            hash,
          },
          event
        );
        break;
      case "event-rec":
        {
          const first_occurrence =
            await controller.event.recurrent.firstOccurrence(id.id);
          if (first_occurrence.value) {
            const occurrenceId = first_occurrence.value.id;
            first_occurrence.release();
            await navigateTo(
              RouteNames.Event,
              router,
              {
                params: { id: occurrenceId },
                query,
                hash,
              },
              event
            );
          }
        }
        break;
      case "page": {
        const target = await resolvePageRouteTarget(controller, id.id);
        await navigateTo(
          target.name,
          router,
          {
            params: target.params,
            query,
            hash,
          },
          event
        );
        break;
      }
      case "people":
        await navigateTo(
          RouteNames.Person,
          router,
          {
            params: { id: id.id },
            query,
            hash,
          },
          event
        );
        break;
      case "object": {
        const { workspace_id, table_id, row_id } =
          await controller.graph.object.id.parse(id.id);
        if (!workspace_id || !table_id || !row_id) return;
        await navigateToCustomObject(
          router,
          workspace_id,
          table_id,
          row_id,
          event,
          query,
          hash
        );
        break;
      }
      case "organization":
        await navigateTo(RouteNames.Organization, router, {
          params: { orgId: id.id },
          query,
          hash,
        });
        break;
      case "table_ref": {
        const { workspace_id: workspaceId, table_id: databaseId } =
          await controller.id.tableRef.parse(id.id);
        await navigateTo(RouteNames.Database, router, {
          params: { workspaceId, databaseId },
          query,
          hash,
        });
        break;
      }
      case "workspace_ref": {
        const { org_id: orgId, workspace_id: workspaceId } =
          await controller.id.workspaceRef.parse(id.id);
        await navigateTo(RouteNames.Workspace, router, {
          params: { orgId, workspaceId },
          query,
          hash,
        });
        break;
      }
      case "agent": {
        const { org_id: organizationId, agent_id: agentId } =
          await controller.id.agentRef.parse(id.id);
        await navigateTo(RouteNames.Agent, router, {
          params: { agentId },
          query: { organizationId },
        });
        break;
      }
      case "automation": {
        const { org_id: organizationId, automation_id: automationId } =
          await controller.id.automationRef.parse(id.id);
        await navigateTo(RouteNames.Automation, router, {
          params: { objectId: automationId },
          query: { organizationId },
        });
        break;
      }
      case "conversation": {
        const {
          org_id: organizationId,
          agent_id: agentId,
          conversation_id: conversationId,
        } = await controller.id.conversationRef.parse(id.id);
        await navigateTo(RouteNames.AgentConversation, router, {
          params: { agentId },
          query: { id: conversationId, organizationId },
        });
        break;
      }
      case "automation_run": {
        const {
          org_id: organizationId,
          automation_id: automationId,
          automation_run_id: runId,
        } = await controller.id.automationRunRef.parse(id.id);
        await navigateTo(RouteNames.AutomationRunDetail, router, {
          params: { objectId: automationId, runId },
          query: { organizationId },
        });
        break;
      }
    }
  }
};

export const navigateToEvent = async (
  router: Router,
  controller: Controller,
  calendarEvent: Event,
  event?: MouseEvent | KeyboardEvent
) => {
  if (calendarEvent.task) {
    await navigateToObject(
      router,
      controller,
      {
        kind: "task",
        id: calendarEvent.task,
      },
      event
    );
  } else {
    await navigateToObject(
      router,
      controller,
      {
        kind: "event",
        id: calendarEvent.id,
      },
      event
    );
  }
};

export async function openRouteInNewWindow(
  router: Router,
  to: RouteLocationRaw | string
): Promise<void> {
  const fullPath = resolveRouteFullPath(router, to);
  if (in_electron) {
    const { ipcRenderer: ipc } = require("electron");
    await ipc.invoke("open-new-window-with-route", {
      fullPath,
    });
  } else {
    window.open(fullPath, "_blank");
  }
}

export const displayedAgendaDate = ref(new Date());
export const displayedJournalDate = ref(new Date());

export const navigateToAgenda = async (
  router: Router,
  date: Date = displayedAgendaDate.value,
  event?: MouseEvent | KeyboardEvent
) => {
  await navigateTo(
    RouteNames.Agenda,
    router,
    {
      query: { date: formatKey(date) },
    },
    event
  );
  displayedAgendaDate.value = date;
};

export const navigateToJournal = async (
  router: Router,
  date: Date = displayedJournalDate.value,
  event?: MouseEvent | KeyboardEvent,
  orgId?: string
) => {
  await navigateTo(
    orgId ? RouteNames.OrgJournal : RouteNames.Journal,
    router,
    {
      ...(orgId && { params: { orgId } }),
      query: { date: formatKey(date) },
    },
    event
  );
  displayedJournalDate.value = date;
};

export const navigateToCalendar = async (
  router: Router,
  date?: Date,
  layout?: PlannerLayout,
  eventId?: string,
  event?: MouseEvent | KeyboardEvent
) => {
  // The target planner reads these query params on mount to focus a date,
  // select a layout, or open an event — instead of us pre-mutating shared
  // state (there is no global planner state anymore; each instance owns its
  // own, see `usePlanner`).
  const query: Record<string, string> = {};
  if (date) query.date = formatKey(date);
  if (eventId) query.event = eventId;
  if (layout) query.layout = layout;
  await navigateTo(
    RouteNames.Calendar,
    router,
    Object.keys(query).length ? { query } : undefined,
    event
  );
};

export const navigateToPlan = async (
  router: Router,
  profile: Profile,
  organizationId?: OrgId,
  event?: MouseEvent | KeyboardEvent
) => {
  const orgId = organizationId || profile.personal.organizationId;
  await navigateTo(
    RouteNames.SettingsPlan,
    router,
    {
      params: { orgId },
    },
    event
  );
};

export const navigateToInternalPath = async (
  controller: Controller,
  router: Router,
  path: string,
  event?: MouseEvent | KeyboardEvent
) => {
  // Allow callers to pass either a raw internal path (`/tasks/123`) or a
  // path with query/hash (`/tasks/123?x=y#z`).
  //
  // Use a base URL so `new URL()` can parse relative paths consistently.
  const url = new URL(
    path.startsWith("/") ? path : `/${path}`,
    "https://app.routine.co"
  );

  const pathname = url.pathname;
  const query = Object.fromEntries(url.searchParams.entries());
  const hasQuery = Object.keys(query).length > 0;
  const hash = url.hash || undefined;

  // Legacy routes rewriting
  if (pathname.startsWith("/tasks/")) {
    const id = pathname.replace("/tasks/", "");
    await navigateToObject(
      router,
      controller,
      { kind: "task", id },
      event,
      hasQuery ? query : undefined,
      hash
    );
  } else if (pathname.startsWith("/recurrent-tasks/")) {
    const id = pathname.replace("/recurrent-tasks/", "");
    await navigateTo(
      RouteNames.RecurrentTask,
      router,
      {
        params: { id },
        ...(hasQuery ? { query } : {}),
        ...(hash ? { hash } : {}),
      },
      event
    );
  } else if (pathname.startsWith("/events/")) {
    const id = pathname.replace("/events/", "");
    await navigateTo(
      RouteNames.Event,
      router,
      {
        params: { id },
        ...(hasQuery ? { query } : {}),
        ...(hash ? { hash } : {}),
      },
      event
    );
  } else if (pathname.startsWith("/pages/")) {
    const id = pathname.replace("/pages/", "");
    await navigateToObject(
      router,
      controller,
      { kind: "page", id },
      event,
      hasQuery ? query : undefined,
      hash
    );
  } else if (pathname.startsWith("/people/")) {
    const id = pathname.replace("/people/", "");
    await navigateTo(
      RouteNames.Person,
      router,
      {
        params: { id },
        ...(hasQuery ? { query } : {}),
        ...(hash ? { hash } : {}),
      },
      event
    );
  } else if (pathname.startsWith("/automations/")) {
    // Run deep links (`/automations/<id>/runs/<runId>`, e.g. the notice
    // links created when a run pauses for user input) must not be parsed as
    // a single automation id (FRONTEND-FH5).
    const runMatch = pathname.match(/^\/automations\/([^/]+)\/runs\/([^/]+)$/);
    if (runMatch) {
      const [, objectId, runId] = runMatch;
      await navigateTo(
        RouteNames.AutomationRunDetail,
        router,
        {
          params: { objectId, runId },
          ...(hasQuery ? { query } : {}),
          ...(hash ? { hash } : {}),
        },
        event
      );
    } else {
      const objectId = pathname.replace("/automations/", "");
      await navigateTo(
        RouteNames.Automation,
        router,
        {
          params: { objectId },
          ...(hasQuery ? { query } : {}),
          ...(hash ? { hash } : {}),
        },
        event
      );
    }
  } else if (/^\/organizations\/[^/]+$/.test(pathname)) {
    const orgId = pathname.replace("/organizations/", "");
    await navigateTo(
      RouteNames.Organization,
      router,
      {
        params: { orgId },
        ...(hasQuery ? { query } : {}),
        ...(hash ? { hash } : {}),
      },
      event
    );
  } else {
    const fullPath = `${pathname}${url.search}${url.hash}`;
    window.console.warn(
      `navigateToInternalPath: Unknown path format, falling back to router.push: ${fullPath}`
    );
    await router.push(fullPath);
  }
};

/** Entity screens' route segment → the reference kind their ids carry. */
const ENTITY_ROUTE_KINDS: Partial<Record<string, ReferenceKind>> = {
  tasks: "task",
  "recurrent-tasks": "task-rec",
  events: "event",
  pages: "page",
  people: "people",
};

/**
 * Convert an internal router path (e.g. `/tasks/<id>` or `/objects/...`) into a typed controller `ReferenceId`.
 *
 * This is useful when we want to store a URL as a `reference` node (which stores `{ kind, id }`)
 * rather than keeping it as a plain hyperlink.
 */
export const idFromInternalPath = async (
  internalPath: string,
  controller: Controller
): Promise<ReferenceId | null> => {
  const url = new URL(
    internalPath.startsWith("/") ? internalPath : `/${internalPath}`,
    "https://app.routine.co"
  );

  const pathname = url.pathname;

  // Entity routes carry the id in their last segment, in two shapes:
  // modern, organization-scoped ones (`/organizations/<orgId>/tasks/<id>`,
  // what `getObjectRoutePath` builds and "Copy link" produces — the
  // organization segment is routing context only, the ids are globally
  // unique) and legacy un-scoped ones (`/tasks/<id>`, links copied before
  // routes became org-scoped and stored payloads such as notifications).
  const entityMatch =
    pathname.match(/^\/organizations\/[^/]+\/([^/]+)\/(.+)$/) ??
    pathname.match(/^\/([^/]+)\/(.+)$/);
  if (entityMatch) {
    const [, segment, id] = entityMatch;
    const kind = ENTITY_ROUTE_KINDS[segment];
    if (kind) return { kind, id };
  }

  // Custom-object screens (`/objects/:workspaceId/:tableId/:rowId`) become
  // graph object references: the three route segments re-encode into the
  // full object id.
  const objectMatch = pathname.match(/^\/objects\/([^/]+)\/([^/]+)\/(.+)$/);
  if (objectMatch) {
    const [, workspaceId, tableId, rowId] = objectMatch;
    const fullId = await controller.graph.object.id.make({
      workspace_id: decodeURIComponent(workspaceId),
      table_id: decodeURIComponent(tableId),
      row_id: decodeURIComponent(rowId),
    });
    return { kind: "object", id: fullId };
  }

  // Workspace pages (`/workspaces/:orgId/:workspaceId`) become workspace
  // references.
  const workspaceMatch = pathname.match(/^\/workspaces\/([^/]+)\/([^/]+)$/);
  if (workspaceMatch) {
    const [, orgId, workspaceId] = workspaceMatch;
    return makeWorkspaceReference(
      decodeURIComponent(orgId),
      decodeURIComponent(workspaceId)
    );
  }

  // Organization screens (`/organizations/:orgId`) become organization
  // references. Org-scoped entity routes (`/organizations/:orgId/tasks/…`)
  // are handled above and must not match here.
  const organizationMatch = pathname.match(/^\/organizations\/([^/]+)$/);
  if (organizationMatch) {
    return makeOrganizationReference(decodeURIComponent(organizationMatch[1]));
  }

  // Agent, automation, and conversation screens may encode the owning
  // organization in the query (`organizationId=`), matching
  // `getObjectRoutePath`. Copy-link URLs for agents and conversations omit
  // it, so fall back to looking the entity up in each organization.
  const conversationMatch = pathname.match(
    /^\/agents\/([^/]+)\/conversations$/
  );
  if (conversationMatch) {
    const agentId = decodeURIComponent(conversationMatch[1]);
    const conversationId = url.searchParams.get("id");
    const organizationId =
      url.searchParams.get("organizationId") ??
      (await organizationIdForAgent(controller, agentId));
    if (organizationId && conversationId) {
      return makeConversationReference(organizationId, agentId, conversationId);
    }
  }

  const agentMatch = pathname.match(/^\/agents\/([^/]+)$/);
  if (agentMatch) {
    const agentId = decodeURIComponent(agentMatch[1]);
    const organizationId =
      url.searchParams.get("organizationId") ??
      (await organizationIdForAgent(controller, agentId));
    if (organizationId) {
      return makeAgentReference(organizationId, agentId);
    }
  }

  const automationMatch = pathname.match(/^\/automations\/([^/]+)$/);
  if (automationMatch) {
    const automationId = decodeURIComponent(automationMatch[1]);
    const organizationId =
      url.searchParams.get("organizationId") ??
      (await organizationIdForAutomation(controller, automationId));
    if (organizationId) {
      return makeAutomationReference(organizationId, automationId);
    }
  }

  // Database screens (`/databases/:workspaceId/:databaseId`) become
  // whole-database (table) references.
  const databaseMatch = pathname.match(/^\/databases\/([^/]+)\/([^/]+)$/);
  if (databaseMatch) {
    const [, workspaceId, databaseId] = databaseMatch;
    return makeTableReference(
      decodeURIComponent(workspaceId),
      decodeURIComponent(databaseId)
    );
  }

  return null;
};

export const navigateToInternalPathOrObject = async (
  controller: Controller,
  router: Router,
  path: string,
  event?: MouseEvent | KeyboardEvent
) => {
  // Notices and open.routine.co links sometimes store a raw Routine id
  // (`object:…`, `task:…`, …) as the path. `new URL("/object:…")` would
  // otherwise treat that as an unknown path and never reach the object
  // screen (which shows Not found when the row is gone).
  const stripped = path.startsWith("/") ? path.slice(1) : path;
  const pathOnly = stripped.split(/[?#]/, 1)[0] ?? stripped;
  const reference = referenceIdFromString(pathOnly);
  if (reference) {
    await navigateToObject(router, controller, reference, event);
    return;
  }
  // Workspace.Id is `workspace:…`, not `workspace_ref:…`. The workspace
  // screen needs the owning org, which is not encoded in the id.
  if (pathOnly.startsWith("workspace:")) {
    const orgId = await organizationIdForWorkspace(controller, pathOnly);
    if (orgId) {
      await navigateTo(
        RouteNames.Workspace,
        router,
        { params: { orgId, workspaceId: pathOnly } },
        event
      );
    }
    return;
  }
  const idPath = internalPathFromRoutineId(pathOnly);
  if (idPath) {
    await navigateToInternalPath(controller, router, idPath, event);
    return;
  }
  if (isRoutinePrefixedId(pathOnly)) return;

  // Allow callers to pass either a raw internal path (`/objects/...`) or a
  // path with query/hash (`/objects/...?...#...`).
  const url = new URL(
    path.startsWith("/") ? path : `/${path}`,
    "https://app.routine.co"
  );

  const pathname = url.pathname;
  const query = Object.fromEntries(url.searchParams.entries());
  const hasQuery = Object.keys(query).length > 0;
  const hash = url.hash || undefined;

  // Handle custom object paths: /objects/workspaceId/tableId/rowId
  const objectMatch = pathname.match(/^\/objects\/([^/]+)\/([^/]+)\/(.+)$/);
  if (objectMatch) {
    const [, workspaceId, tableId, rowId] = objectMatch;
    await navigateToCustomObject(
      router,
      workspaceId,
      tableId,
      rowId,
      event,
      hasQuery ? query : undefined,
      hash
    );
    return;
  }

  // Delegate to navigateToInternalPath for non-object paths (preserve query/hash).
  await navigateToInternalPath(
    controller,
    router,
    `${pathname}${url.search}${url.hash}`,
    event
  );
};

if (typeof window !== "undefined") {
  Reflect.set(window, "debugHistory", history.dump);
  Reflect.set(window, "debugScrolling", scrolling.dump);
}
